/**
 * Authentication Context
 * Simple authentication state management
 * Supports token-based authentication via query parameter
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import agentAPI from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmbedded, setIsEmbedded] = useState(false);

  // Define logout first so it can be used in useEffect
  const logout = useCallback(() => {
    setIsAuthenticated(false);
    localStorage.removeItem('gibsGuruAuth');
    localStorage.removeItem('gibsGuruAuthTime');
    localStorage.removeItem('gibsGuruAuthToken');
  }, []);

  const login = () => {
    setIsAuthenticated(true);
    localStorage.setItem('gibsGuruAuth', 'true');
    localStorage.setItem('gibsGuruAuthTime', Date.now().toString());
  };

  // Check for existing session or token on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // TEMPORARY: Bypass auth for local development
        // Check if BYPASS_AUTH is enabled (set in .env file as REACT_APP_BYPASS_AUTH=true)
        if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
          console.log('Auth bypass active - skipping authentication');
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // First, check for token in URL query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (token) {
          // Validate the token with the backend — retry on network failures
          // (backend cold start can cause the first request to fail/timeout)
          let validation = null;
          for (let attempt = 1; attempt <= 3; attempt++) {
            validation = await agentAPI.validateToken(token);
            if (validation.valid) break;
            // Only retry on likely transient failures (network error, not "invalid token")
            const isTransient = validation.message && (
              validation.message.includes('Failed to fetch') ||
              validation.message.includes('NetworkError') ||
              validation.message.includes('timeout') ||
              validation.message === 'Token validation failed'
            );
            if (!isTransient || attempt === 3) break;
            // Back off before retrying
            await new Promise(r => setTimeout(r, attempt * 1000));
          }

          if (validation.valid) {
            // Token is valid, authenticate the user
            setIsAuthenticated(true);
            setIsEmbedded(true); // Mark as embedded since they used token auth
            localStorage.setItem('gibsGuruAuth', 'true');
            localStorage.setItem('gibsGuruAuthTime', Date.now().toString());
            localStorage.setItem('gibsGuruAuthToken', token);

            // Remove token from URL for security (clean the browser history)
            const cleanUrl = window.location.pathname + window.location.hash;
            window.history.replaceState({}, document.title, cleanUrl);

            setIsLoading(false);
            return;
          } else {
            // Token is invalid, clear any existing auth and show error
            console.error('Invalid token provided');
            logout();
            setIsLoading(false);
            return;
          }
        }

        // No token in URL, check for existing session in localStorage
        const authStatus = localStorage.getItem('gibsGuruAuth');
        const authTimestamp = localStorage.getItem('gibsGuruAuthTime');
        const storedToken = localStorage.getItem('gibsGuruAuthToken');

        if (authStatus === 'true' && authTimestamp) {
          const loginTime = parseInt(authTimestamp, 10);
          const now = Date.now();
          const eightHours = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

          // Check if session is still valid (less than 8 hours old)
          if (now - loginTime < eightHours) {
            setIsAuthenticated(true);
            // If there's a stored token, they're in embedded mode
            if (storedToken) {
              setIsEmbedded(true);
            }
          } else {
            // Session expired, clear it
            logout();
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [logout]);

  const value = {
    isAuthenticated,
    isLoading,
    isEmbedded,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};